import React, { useEffect, useMemo, useState } from 'react';
import { API } from '../../lib/api';
import axios from 'axios';
import { Clapperboard, CheckCircle2, XCircle, Trash2, RefreshCw, Film, Flag, ShieldCheck, Clock, Eye, Play, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { extractYtId } from '../../components/YouTubeShortPlayer';

const TABS = [
  { k: 'pending', label: 'En attente', icon: Clock, cl: 'text-amber-300' },
  { k: 'approved', label: 'Publiés', icon: CheckCircle2, cl: 'text-emerald-300' },
  { k: 'rejected', label: 'Refusés', icon: XCircle, cl: 'text-red-300' },
];

function Preview({ s, playing, onPlay }) {
  const ytId = s.source_type === 'link' ? extractYtId(s.embed_url || s.video_url) : null;
  if (s.source_type === 'file') {
    return <video src={`${process.env.REACT_APP_BACKEND_URL}${s.media_url}`} controls playsInline className="w-full aspect-video max-h-[220px] object-contain bg-black" />;
  }
  if (ytId && !playing) {
    return (
      <button type="button" onClick={onPlay} data-testid={`shorts-mod-thumb-${s.id}`}
        className="relative block w-full aspect-video bg-black group">
        <img src={`https://i.ytimg.com/vi/${ytId}/hqdefault.jpg`} alt={s.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition" loading="lazy" />
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="w-12 h-12 rounded-full bg-red-600/90 flex items-center justify-center shadow-lg"><Play size={18} className="text-white ml-0.5" /></span>
        </span>
      </button>
    );
  }
  if (s.embed_url) {
    return <iframe src={`${s.embed_url}${s.embed_url.includes('?') ? '&' : '?'}autoplay=1`} title={s.title} className="w-full aspect-video bg-black" allow="autoplay; encrypted-media" allowFullScreen />;
  }
  return (
    <a href={s.video_url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full aspect-video bg-black text-sky-300 text-[13px] font-semibold">
      <ExternalLink size={14} /> Ouvrir sur {s.platform} ↗
    </a>
  );
}

export default function ShortsModerationAdmin() {
  const [tab, setTab] = useState('pending');
  const [all, setAll] = useState([]);
  const [busy, setBusy] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const token = localStorage.getItem('cv_token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = () => {
    setBusy(true);
    axios.get(`${API}/shorts/admin/list?status=all`, { headers })
      .then((r) => setAll(r.data))
      .catch(() => toast.error('Chargement impossible'))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const counts = useMemo(() => ({
    pending: all.filter((s) => s.status === 'pending').length,
    approved: all.filter((s) => s.status === 'approved').length,
    rejected: all.filter((s) => s.status === 'rejected').length,
    views: all.filter((s) => s.status === 'approved').reduce((a, s) => a + (s.views || 0), 0),
    reports: all.reduce((a, s) => a + (s.reports || 0), 0),
  }), [all]);
  const items = useMemo(() => all.filter((s) => s.status === tab), [all, tab]);

  const act = async (id, action) => {
    try {
      if (action === 'approve') await axios.post(`${API}/shorts/admin/${id}/approve`, {}, { headers });
      if (action === 'reject') {
        const reason = window.prompt('Motif du refus (visible par l\'auteur) :');
        if (reason === null) return;
        await axios.post(`${API}/shorts/admin/${id}/reject`, { reason }, { headers });
      }
      if (action === 'delete') {
        if (!window.confirm('Retirer ce contenu ? (il disparaît du site, trace conservée)')) return;
        await axios.delete(`${API}/shorts/admin/${id}`, { headers });
      }
      toast.success('Fait — l\'auteur est notifié par email');
      load();
    } catch {
      toast.error('Action impossible');
    }
  };

  return (
    <div data-testid="shorts-moderation-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <h1 className="font-serif-editorial text-white text-[24px] sm:text-[30px] inline-flex items-center gap-2.5">
          <Clapperboard size={22} className="text-red-400" /> Shorts communauté
        </h1>
        <button type="button" onClick={load} data-testid="shorts-mod-refresh"
          className="h-9 px-3 rounded-full border border-white/15 bg-white/5 text-white/70 text-[12px] font-semibold inline-flex items-center gap-1.5">
          <RefreshCw size={13} className={busy ? 'animate-spin' : ''} /> Actualiser
        </button>
      </div>

      {/* Vue d'ensemble */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="shorts-mod-overview">
        {[
          { label: 'En attente', val: counts.pending, icon: Clock, cl: 'text-amber-300 border-amber-600/30 bg-amber-950/15' },
          { label: 'Publiés', val: counts.approved, icon: CheckCircle2, cl: 'text-emerald-300 border-emerald-600/30 bg-emerald-950/15' },
          { label: 'Vues cumulées', val: counts.views, icon: Eye, cl: 'text-white/80 border-white/10 bg-white/[0.03]' },
          { label: 'Signalements', val: counts.reports, icon: Flag, cl: counts.reports > 0 ? 'text-amber-300 border-amber-600/40 bg-amber-950/25' : 'text-white/50 border-white/10 bg-white/[0.03]' },
        ].map(({ label, val, icon: Icon, cl }) => (
          <div key={label} className={`rounded-2xl border p-3.5 ${cl}`}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold font-mono-spec opacity-70"><Icon size={12} /> {label}</div>
            <div className="font-serif-editorial text-[28px] leading-none mt-1.5">{val}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 mb-5 flex-wrap">
        {TABS.map((t) => (
          <button key={t.k} type="button" onClick={() => { setTab(t.k); setPlayingId(null); }} data-testid={`shorts-mod-tab-${t.k}`}
            className={`h-10 px-4 rounded-full text-[13px] font-semibold border transition inline-flex items-center gap-1.5 ${tab === t.k ? 'bg-red-600 border-red-500 text-white' : 'bg-white/[0.03] border-white/10 text-white/60'}`}>
            {t.label}
            <span className={`px-1.5 py-0.5 rounded-full text-[10.5px] font-bold ${tab === t.k ? 'bg-white/20' : 'bg-white/10'}`}>{counts[t.k]}</span>
          </button>
        ))}
      </div>

      {items.length === 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center text-white/40 text-[13.5px]" data-testid="shorts-mod-empty">
          <Film size={22} className="mx-auto mb-2 opacity-40" /> Aucun short {tab === 'pending' ? 'en attente' : tab === 'approved' ? 'publié' : 'refusé'}.
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((s) => (
          <div key={s.id} className="rounded-2xl border border-white/10 bg-[#0d0d0e] overflow-hidden flex flex-col" data-testid={`shorts-mod-card-${s.id}`}>
            <Preview s={s} playing={playingId === s.id} onPlay={() => setPlayingId(s.id)} />
            <div className="p-4 flex-1 flex flex-col">
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <span className="px-2 py-0.5 rounded-full bg-red-500/10 border border-red-500/30 text-red-300 text-[10px] font-bold uppercase">{s.theme}</span>
                <span className="text-white/40 text-[11px]">par {s.author_name} ({s.author_role})</span>
                {s.cgu_accepted_at && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] font-bold" title={`Conditions acceptées le ${new Date(s.cgu_accepted_at).toLocaleString('fr-FR')}`} data-testid={`shorts-mod-cgu-${s.id}`}>
                    <ShieldCheck size={10} /> CGU
                  </span>
                )}
                {s.status === 'approved' && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/[0.06] border border-white/15 text-white/70 text-[10px] font-bold" data-testid={`shorts-mod-views-${s.id}`}>
                    <Eye size={10} /> {s.views || 0}
                  </span>
                )}
                {s.reports > 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/40 text-amber-300 text-[10px] font-bold" data-testid={`shorts-mod-reports-${s.id}`}>
                    <Flag size={10} /> {s.reports} signalement{s.reports > 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <div className="text-white text-[14px] font-bold leading-snug">{s.title}</div>
              {s.description && <p className="text-white/55 text-[12px] mt-1 leading-snug">{s.description}</p>}
              <div className="text-white/35 text-[10.5px] font-mono-spec mt-1.5">
                Proposé le {new Date(s.created_at).toLocaleDateString('fr-FR')}
                {s.reviewed_at ? ` · traité le ${new Date(s.reviewed_at).toLocaleDateString('fr-FR')}` : ''}
              </div>
              {s.reject_reason && <p className="text-red-300 text-[11.5px] mt-1">Motif refus : {s.reject_reason}</p>}
              {(s.history || []).length > 0 && (
                <div className="mt-2 text-white/35 text-[10.5px] font-mono-spec space-y-0.5" data-testid={`shorts-mod-history-${s.id}`}>
                  {s.history.slice(-3).map((h, i) => (
                    <div key={i}>• {h.action} par {h.by || '—'} · {new Date(h.at).toLocaleDateString('fr-FR')}{h.reason ? ` (${h.reason})` : ''}</div>
                  ))}
                </div>
              )}
              <div className="mt-auto pt-3 flex gap-2">
                {s.status !== 'approved' && (
                  <button type="button" onClick={() => act(s.id, 'approve')} data-testid={`shorts-mod-approve-${s.id}`}
                    className="flex-1 h-9 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-[12px] font-bold inline-flex items-center justify-center gap-1.5">
                    <CheckCircle2 size={13} /> Publier
                  </button>
                )}
                {s.status !== 'rejected' && (
                  <button type="button" onClick={() => act(s.id, 'reject')} data-testid={`shorts-mod-reject-${s.id}`}
                    className="flex-1 h-9 rounded-full border border-red-600/50 text-red-300 text-[12px] font-bold inline-flex items-center justify-center gap-1.5 hover:bg-red-950/30">
                    <XCircle size={13} /> Refuser
                  </button>
                )}
                <button type="button" onClick={() => act(s.id, 'delete')} data-testid={`shorts-mod-delete-${s.id}`}
                  className="h-9 w-9 rounded-full border border-white/15 text-white/50 inline-flex items-center justify-center hover:bg-white/10">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
